import type {
  AssistantActionItemsResponse,
  AssistantAnalysisRow,
  AssistantBriefDto,
  AssistantJobDto,
  AssistantRefreshProgress,
  MeResponse,
  MeetingSlackContextDigest,
  SlackTodayCitationDto,
  SlackTodayOffReason,
  SlackTodayResponse,
  SlackTodayDoneSignalDto,
  SlackTodaySweepDto,
  SlackTodayUpdateDto,
  TodoDto,
} from '../../../shared/api-types.js';
import {
  ASSISTANT_BRIEF_WAIT_LABELS,
  SLACK_PRIOR_VERDICT_LABELS,
  SLACK_SWEEP_PUBLIC_SEARCH_LABELS,
  SLACK_SWEEP_UPDATE_STATUS_LABELS,
  type AssistantBriefWait,
  type AssistantLaterSide,
  type AssistantThreadStatus,
  type SlackFindingKind,
  type SlackPartialReason,
  type SlackPriorVerdict,
  type SlackSweepUpdateStatus,
} from '../../../shared/enums.js';
import { briefRefMeetingId, type AssistantBriefComparedWith, type AssistantBriefPayload, type AssistantBriefPriorReview, type AssistantBriefPriorSubject, type AssistantBriefRef } from '../../../shared/assistant.js';
import { PATHS } from '../../paths.js';
import type { IconName } from '../../ui/Icon.js';
import { formatCount, formatDateTime, pluralise } from '../../ui/format.js';
import type { Tone } from '../../ui/StatusPill.js';
import { slackPermalinkHref, slackTodayDomId, slackTodayHref } from './slack-context.js';
import { meetingTodoSearch, SLACK_DAY_LABEL, todoWithoutMeetingLabel } from './todos.js';

// The Assistant page's pure helpers (owner rulings A1–A9, A11a of 2026-09-27; design §4): what the
// General Intelligence tab says about where its brief came from and whether one may be asked for now,
// what the head says of a refresh under way, how a reference in the brief becomes a link, how the
// Slack today tab reads (owner request of 2026-09-30, the daily Slack sweep), and how the Analysis tab's
// rows read. DOM-free; unit-tested under node.

/** The tabs, in order. Slack today sits beside the brief it keeps current (map A10: a tab of equal standing). */
export const ASSISTANT_TABS = ['intelligence', 'slack-today', 'action-items', 'analysis'] as const;
export type AssistantTabId = (typeof ASSISTANT_TABS)[number];

/**
 * The tabs this installation offers: Slack today only where the daily Slack sweep runs (`me.integrations.slackSweep`
 * not `off`) — elsewhere it could only ever say the daily look is not switched on; an address naming it there falls
 * back to the first tab rather than to a tab that is not there.
 */
export function assistantTabs(slackShown: boolean): readonly AssistantTabId[] {
  return slackShown ? ASSISTANT_TABS : ASSISTANT_TABS.filter((tab) => tab !== 'slack-today');
}

// --- General Intelligence ----------------------------------------------------------------------------

/**
 * A sentence of the brief as plain text: the emphasis markers a summary's own words may carry
 * (`**bold**`, `` `code` ``) are dropped, never rendered — the brief is prose, not markup.
 */
export function briefPlainText(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/gu, '$1').replace(/`([^`]+)`/gu, '$1');
}

/**
 * "from 14 meetings (and Slack)" — what the brief drew on; when the length limit left the oldest
 * meetings out, it says so: "from 14 of 17 meetings (and Slack) — the 3 oldest were left out to fit".
 */
export function briefCoverageText(brief: Pick<AssistantBriefDto, 'meetingsConsidered' | 'meetingsDropped' | 'usedSlack'>): string {
  const total = brief.meetingsConsidered + brief.meetingsDropped;
  // Why each form whole (05-content §8; the record's "brief's coverage line", Phase 1c D30): " (and Slack)" was spliced
  // onto each by condition.
  if (brief.meetingsDropped === 0) {
    return brief.usedSlack
      ? pluralise(brief.meetingsConsidered, { one: 'from # meeting (and Slack)', other: 'from # meetings (and Slack)' })
      : pluralise(brief.meetingsConsidered, { one: 'from # meeting', other: 'from # meetings' });
  }
  // The clause about what was left out is whole in each form, chosen by count (05-content §8).
  const dropped = pluralise(brief.meetingsDropped, { one: 'the oldest was left out to fit', other: 'the # oldest were left out to fit' });
  const of = pluralise(total, { one: '# meeting', other: '# meetings' });
  return brief.usedSlack
    ? `from ${formatCount(brief.meetingsConsidered)} of ${of} (and Slack) — ${dropped}`
    : `from ${formatCount(brief.meetingsConsidered)} of ${of} — ${dropped}`;
}

export type RefreshNowOffer =
  /** A brief is waiting to be written: due at `runAfter` (new material settles first). */
  | { kind: 'queued'; text: string; runAfter: string }
  | { kind: 'running'; text: string }
  /** The person asked a moment ago; the reason stands where the button would. */
  | { kind: 'cooldown'; text: string; until: string }
  | { kind: 'ready' };

/**
 * Whether "Refresh now" may be offered, or what stands in its place (03-components §2: never a disabled
 * button as the only explanation). A queued brief the person may still bring forward stays an offer
 * unless the cooldown holds. `refreshInFlight`: the person's own "Refresh recent meetings" is under way.
 */
export function refreshNowOffer(input: { job: AssistantJobDto | null; cooldownUntil: string | null; now: Date; refreshInFlight?: boolean }): RefreshNowOffer {
  const { job, cooldownUntil, now } = input;
  if (job?.status === 'running') return { kind: 'running', text: job.attempts > 1 ? 'Running · the first attempt failed; Calis is trying again' : 'Running · Calis is writing your brief' };
  const pending = job?.status === 'queued' ? `Pending · ${pendingText(job, now, input.refreshInFlight === true)}` : null;
  if (cooldownUntil !== null && Date.parse(cooldownUntil) > now.getTime()) {
    const wait = pluralise(Math.max(1, Math.ceil((Date.parse(cooldownUntil) - now.getTime()) / 60_000)), { one: '# minute', other: '# minutes' });
    // A brief already queued for later is still said, so the same queue reads the same before and after the cooldown.
    return { kind: 'cooldown', text: pending ? `${pending} — you can bring it forward in ${wait}.` : `Refreshed a moment ago — you can refresh again in ${wait}.`, until: cooldownUntil };
  }
  if (pending && job?.status === 'queued') return { kind: 'queued', text: pending, runAfter: job.runAfter };
  return { kind: 'ready' };
}

/**
 * What a queued brief is waiting for, from what the server counted (production v1.65: "due any moment
 * now" was said from the brief's own due time alone, while a refresh's forty jobs ran first; owner ruling
 * 4 of 2026-09-28: with several slots the line counts the jobs that will start before the brief, and says
 * when it waits for the person's own job in progress or for a free slot). "Any moment now" only when
 * nothing holds it; the refresh is named only when it is the person's own and the brief is an automatic
 * one, which waits for the refresh by design (A11a) — a brief they asked for goes ahead of it. Between two
 * attempts it says the first one failed.
 */
function pendingText(job: AssistantJobDto, now: Date, refreshInFlight: boolean): string {
  const dueMs = Date.parse(job.runAfter) - now.getTime();
  const ahead = Math.max(0, job.jobsAhead);
  const clause = waitClause(ahead, job.waitingFor);
  if (job.attempts > 0) {
    // Why the time first while the retry waits: its back-off or the API's Retry-After (up to 15 minutes)
    // outlasts the work in flight, and meanwhile the workers run other work — "once 1 earlier job
    // finishes" stayed true of the count and false of the brief. What holds it is said once it is due.
    if (dueMs > 60_000) return `the first attempt failed; trying again ${dueIn(dueMs)}`;
    return `the first attempt failed; trying again ${clause ? `— ${clause}` : dueIn(dueMs)}`;
  }
  if (refreshInFlight && job.trigger !== 'manual' && clause) return `${clause} (the refresh of your meetings comes first)`;
  // Why running work is not named before the brief is due: the job in progress may well be over by then;
  // the jobs queued ahead of it are the ones still to come first.
  if (dueMs > 60_000) return ahead > 0 ? `a new brief is due ${dueIn(dueMs)} at the earliest; ${startBefore(ahead)}` : `a new brief is due ${dueIn(dueMs)}`;
  return clause ?? `a new brief is due ${dueIn(dueMs)}`;
}

/** "3 jobs will start before your brief". */
function startBefore(ahead: number): string {
  return pluralise(ahead, { one: '# job will start before your brief', other: '# jobs will start before your brief' });
}

/** What holds a due brief, or null when nothing does: the running work it waits for, then the jobs that start first. */
function waitClause(ahead: number, waitingFor: AssistantBriefWait | null): string | null {
  const waiting = waitingFor === null ? null : `waiting for ${ASSISTANT_BRIEF_WAIT_LABELS[waitingFor]}`;
  if (waiting && ahead > 0) return `${waiting}; ${startBefore(ahead)}`;
  return waiting ?? (ahead > 0 ? startBefore(ahead) : null);
}

/** "any moment now", or "in about 4 minutes". */
function dueIn(ms: number): string {
  return ms <= 60_000 ? 'any moment now' : `in about ${pluralise(Math.ceil(ms / 60_000), { one: '# minute', other: '# minutes' })}`;
}

const THREAD_STATUS_PRESENTATION: Readonly<Record<AssistantThreadStatus, { tone: Tone; icon: IconName }>> = {
  on_track: { tone: 'success', icon: 'check' },
  at_risk: { tone: 'warning', icon: 'alert-triangle' },
  blocked: { tone: 'danger', icon: 'x' },
  waiting: { tone: 'info', icon: 'clock' },
  done: { tone: 'neutral', icon: 'check' },
  unclear: { tone: 'neutral', icon: 'help-circle' },
};

/**
 * The qualifiers naming what each reading reads, shown beside its pill (05-content §6.1, ruled A25 in design system 8.5.0:
 * a verdict, an assessment or a position "is worded as a sentence, or carries a qualifier naming what is being read").
 * Why beside the pill and not in its words: the words stay plain phrases the daily sweep's prompt reads back lowercased
 * (ai/slack-sweep/prompt.ts), and a bare "On track" or "Settled" in a state-styled pill reads as a record's state — the
 * conformity review of 2026-10-05 found them so. A thread's pill reads its progress; an update's, what Slack shows.
 */
export const THREAD_READING_QUALIFIER = 'Progress';
export const UPDATE_READING_QUALIFIER = 'In Slack';

/** A thread's status pill: never colour alone (01-foundations §7.3). */
export function threadStatusPresentation(status: AssistantThreadStatus): { tone: Tone; icon: IconName } {
  return THREAD_STATUS_PRESENTATION[status];
}

/** Which side of an inconsistency reads as the later one: labels for the two blocks. */
export function inconsistencySideLabels(whichIsLater: AssistantLaterSide): { a: string; b: string } {
  if (whichIsLater === 'a') return { a: 'Said later', b: 'Said earlier' };
  if (whichIsLater === 'b') return { a: 'Said earlier', b: 'Said later' };
  return { a: 'One side', b: 'The other' };
}

// Where a daily sweep's finding is shown — its card on the Assistant's Slack today tab — and its element's id: one rule
// with the way back from a sweep's to-do, so they live in lib/slack-context.ts (which this module already builds on).
export { slackTodayDomId, slackTodayHref };

/**
 * Whether the reader can open the Assistant's "Slack today" tab: the Assistant offered to them (AI summaries under its
 * floor) and the daily sweep running here. Why it is asked: the sweep reads the day of anyone with Slack access (owner,
 * round A), so a person may hold a sweep's to-do whose evidence they cannot open; the page then shows the reason alone,
 * never a link that would only refuse them. Selection only: the route decides.
 */
export function canOpenSlackToday(me: Pick<MeResponse, 'capabilities' | 'integrations'>): boolean {
  return me.capabilities.aiSummaries && me.integrations.assistant !== 'off' && me.integrations.slackSweep !== 'off';
}

/** What the page knows the brief's references by: meeting titles and to-do texts, by id, and the daily sweep's findings by their words. */
export interface BriefNames {
  meetings: ReadonlyMap<string, string>;
  todos: ReadonlyMap<string, string>;
  /** The latest look's matters (`matter`: its title) and updates (`update`: the open item's words), by id; absent before it loads. */
  sweep?: ReadonlyMap<string, { kind: 'matter' | 'update'; text: string }>;
  /**
   * The Slack message each Slack reference rests on, as an href that passed `slackPermalinkHref`, by `briefRefSlackKey`:
   * a meeting check's finding as the server resolved it with the brief, a daily sweep's finding from the Slack today
   * reading. Absent or empty where there is none to open.
   */
  slackLinks?: ReadonlyMap<string, string>;
}

/**
 * What the page knows of where a brief's entries lead: the Analysis rows' titles, the to-dos the brief refers to that
 * are still there (as the server resolved them), what the Slack today tab holds (the page reads it already, for the
 * tab's count), and the Slack messages the brief's Slack references rest on.
 */
export function briefNames(
  rows: readonly Pick<AssistantAnalysisRow, 'id' | 'title'>[],
  brief: Pick<AssistantBriefDto, 'referencedTodos' | 'slackLinks'> | null,
  sweep: Pick<SlackTodaySweepDto, 'matters' | 'updates' | 'citations'> | null = null,
): BriefNames {
  const slackLinks = new Map<string, string>();
  for (const link of brief?.slackLinks ?? []) {
    const href = slackPermalinkHref(link.permalink);
    if (href) slackLinks.set(briefRefSlackKey({ type: 'finding', meetingId: link.meetingId, findingId: link.findingId }), href);
  }
  for (const item of [...(sweep?.matters ?? []), ...(sweep?.updates ?? [])]) {
    const href = firstSlackHref(item.citationIds, sweep?.citations ?? []);
    if (href) slackLinks.set(briefRefSlackKey({ type: 'sweep_finding', findingId: item.id }), href);
  }
  return {
    meetings: meetingTitles(rows),
    todos: new Map((brief?.referencedTodos ?? []).map((todo) => [todo.id, todo.text])),
    sweep: new Map<string, { kind: 'matter' | 'update'; text: string }>([
      ...(sweep?.matters ?? []).map((matter): [string, { kind: 'matter' | 'update'; text: string }] => [matter.id, { kind: 'matter', text: matter.title }]),
      ...(sweep?.updates ?? []).map((update): [string, { kind: 'matter' | 'update'; text: string }] => [update.id, { kind: 'update', text: update.subject }]),
    ]),
    slackLinks,
  };
}

// --- Where an entry leads, and the Slack message it rests on (owner request of 2026-10-01) -------------
//
// Why one place per entry (the owner, 2026-10-01: "too many words and links for each item"): an entry used to end in
// a line of every place it rested on, each named in full. Now its own words are the one way to where it belongs — the
// to-do, or the meeting's summary — said in a short line beside it, and the Slack message it rests on is one "Open in
// Slack" link straight to Slack. The places beyond the first are left to that page.

/** Where an entry's words lead, and what the short line beside it says of that place. */
export interface EntryTarget {
  href: string;
  kind: 'todo' | 'meeting' | 'slack_today';
  /** "To-do · Weekly sync", "Meeting summary · Weekly sync", "Slack today". */
  label: string;
}

/** A to-do: on its meeting's To-dos tab, highlighted, or — tied to no meeting — on the To-dos page, whatever its state. */
export function todoTarget(todoId: string, meeting: { id: string; title: string | null } | null): EntryTarget {
  if (meeting === null) return { href: slackTodayTodoHref(todoId), kind: 'todo', label: 'To-do' };
  return { href: `${PATHS.meeting(meeting.id)}${meetingTodoSearch(todoId)}`, kind: 'todo', label: meeting.title ? `To-do · ${meeting.title}` : 'To-do' };
}

/** A meeting: its page opens on the summary (the meeting page's first tab). */
export function meetingTarget(meetingId: string, title: string | null): EntryTarget {
  return { href: PATHS.meeting(meetingId), kind: 'meeting', label: title ? `Meeting summary · ${title}` : 'Meeting summary' };
}

/**
 * Where an entry of the brief leads: the to-do it rests on, or the summary of the meeting it rests on (a Slack
 * finding's meeting included) — `prefer` says which comes first: a commitment is a to-do, a matter or a watch-out is
 * about a meeting. A to-do leads only while it is there (`names.todos`: the server resolves the brief's to-dos as they
 * stand, and one deleted since is absent), so an entry never opens on a to-do that is gone — its meeting stands in. A
 * daily sweep's finding leads to its card on Slack today, and only while that card is there. Null when the entry rests
 * on nothing the page can open.
 */
export function briefEntryTarget(refs: readonly AssistantBriefRef[], names: BriefNames, prefer: 'todo' | 'meeting'): EntryTarget | null {
  const todo = refs.find((ref): ref is Extract<AssistantBriefRef, { type: 'todo' }> => ref.type === 'todo' && names.todos.has(ref.todoId));
  const meetingId = refs.map(briefRefMeetingId).find((id): id is string => id !== null) ?? null;
  const sweep = refs.find((ref): ref is Extract<AssistantBriefRef, { type: 'sweep_finding' }> => ref.type === 'sweep_finding' && names.sweep?.has(ref.findingId) === true);
  const toTodo = todo ? todoTarget(todo.todoId, todo.meetingId === null ? null : { id: todo.meetingId, title: names.meetings.get(todo.meetingId) ?? null }) : null;
  const toMeeting = meetingId !== null ? meetingTarget(meetingId, names.meetings.get(meetingId) ?? null) : null;
  const toSweep: EntryTarget | null = sweep ? { href: slackTodayHref(sweep.findingId), kind: 'slack_today', label: 'Slack today' } : null;
  return (prefer === 'todo' ? toTodo ?? toMeeting : toMeeting ?? toTodo) ?? toSweep;
}

/** The key a reference's Slack message is known by in `BriefNames.slackLinks` (only Slack references are ever there). */
export function briefRefSlackKey(ref: AssistantBriefRef): string {
  switch (ref.type) {
    case 'finding':
      return `finding:${ref.meetingId}:${ref.findingId}`;
    case 'sweep_finding':
      return `sweep:${ref.findingId}`;
    case 'todo':
      return `todo:${ref.todoId}`;
    case 'meeting':
    default:
      return `meeting:${ref.meetingId}`;
  }
}

/** The Slack message an entry rests on: the first of its Slack references whose message the page can open, or null. */
export function briefSlackHref(refs: readonly AssistantBriefRef[], names: BriefNames): string | null {
  for (const ref of refs) {
    if (ref.type !== 'finding' && ref.type !== 'sweep_finding') continue;
    const href = names.slackLinks?.get(briefRefSlackKey(ref));
    if (href) return href;
  }
  return null;
}

/** The first of the messages an entry cites that may be opened in Slack (`slackPermalinkHref`), in the order cited. */
export function firstSlackHref(citationIds: readonly string[], citations: readonly Pick<SlackTodayCitationDto, 'id' | 'permalink'>[]): string | null {
  const byId = new Map(citations.map((citation) => [citation.id, citation]));
  for (const id of citationIds) {
    const href = slackPermalinkHref(byId.get(id)?.permalink ?? null);
    if (href) return href;
  }
  return null;
}

/** Where an action item's words lead: the to-do, on its meeting or — tied to none — on the To-dos page. */
export function actionItemTarget(todo: Pick<TodoDto, 'id' | 'meeting'>): EntryTarget {
  return todoTarget(todo.id, todo.meeting ? { id: todo.meeting.id, title: todo.meeting.title } : null);
}

/**
 * The Slack message an action item rests on: a meeting check's, as the server resolved it (`AssistantActionItemsResponse.slackLinks`),
 * else — for the daily sweep's to-do, or a to-do the sweep noted — the first message its finding or note cites in the
 * latest look. Null when there is none the page can open.
 */
export function actionItemSlackHref(
  todo: Pick<TodoDto, 'id' | 'fromSweep' | 'findingIds' | 'notes'>,
  serverLinks: ReadonlyMap<string, string>,
  sweep: Pick<SlackTodaySweepDto, 'matters' | 'todoNotes' | 'citations'> | null,
): string | null {
  const fromServer = slackPermalinkHref(serverLinks.get(todo.id) ?? null);
  if (fromServer) return fromServer;
  if (sweep === null) return null;
  const cited = (id: string): readonly string[] => sweep.matters.find((matter) => matter.id === id)?.citationIds ?? sweep.todoNotes.find((note) => note.id === id)?.citationIds ?? [];
  const ids = [
    ...(todo.fromSweep ? todo.findingIds : []),
    ...todo.notes.filter((note) => note.kind === 'sweep').flatMap((note) => note.findingIds),
  ];
  for (const id of ids) {
    const href = firstSlackHref(cited(id), sweep.citations);
    if (href) return href;
  }
  return null;
}

const REF_TEXT_MAX = 60;

/** An entry's words short enough to name it in a link's description, cut at a word with an ellipsis when they run long. */
export function shortRefText(text: string): string {
  const tidy = text.replace(/\s+/gu, ' ').trim();
  if (tidy.length <= REF_TEXT_MAX) return tidy;
  const cut = tidy.slice(0, REF_TEXT_MAX);
  return `${(cut.includes(' ') ? cut.slice(0, cut.lastIndexOf(' ')) : cut).replace(/[\s,;:.]+$/u, '')}…`;
}

// --- Since your last brief (owner ruling A13) --------------------------------------------------------

/** The verdicts that changed something, in the order the line and the disclosure show them. */
const CHANGED_VERDICT_ORDER: readonly SlackPriorVerdict[] = ['resolved', 'overtaken', 'no_longer_relevant'];

export interface BriefReviewCounts {
  /** Entries of the last brief now resolved, overtaken or no longer relevant. */
  resolved: number;
  /** Entries of this brief the last one did not have. */
  new: number;
  /** Entries of the last brief that still hold. */
  stillOpen: number;
  unclear: number;
}

/** What changed since the last brief, as counts; null when this brief judged no earlier one. */
export function briefReviewCounts(payload: Pick<AssistantBriefPayload, 'priorReview' | 'comparedWith'>): BriefReviewCounts | null {
  if (payload.priorReview === null || payload.comparedWith === null) return null;
  const of = (verdict: SlackPriorVerdict) => payload.priorReview!.filter((entry) => entry.verdict === verdict).length;
  return { resolved: CHANGED_VERDICT_ORDER.reduce((sum, verdict) => sum + of(verdict), 0), new: payload.comparedWith.newEntries, stillOpen: of('still_holds'), unclear: of('unclear') };
}

/**
 * "3 settled · 2 new · 4 still open" — the line under the headline; "· 1 could not be judged" when any
 * verdict was unclear, so every entry of the last brief is counted somewhere on the line. "Settled"
 * rather than "resolved": the count lumps resolved, overtaken and no longer relevant together, which the
 * disclosure beneath tells apart.
 */
export function sinceLastBriefText(counts: BriefReviewCounts): string {
  const parts = [`${formatCount(counts.resolved)} settled`, `${formatCount(counts.new)} new`, `${formatCount(counts.stillOpen)} still open`];
  if (counts.unclear > 0) parts.push(`${formatCount(counts.unclear)} could not be judged`);
  return parts.join(' · ');
}

export interface BriefPriorReviewGroups {
  /** Resolved, then overtaken, then no longer relevant — each in the brief's own order. */
  changed: AssistantBriefPriorReview[];
  unclear: AssistantBriefPriorReview[];
  stillHolds: AssistantBriefPriorReview[];
}

export function groupBriefPriorReview(entries: readonly AssistantBriefPriorReview[]): BriefPriorReviewGroups {
  const of = (verdict: SlackPriorVerdict) => entries.filter((entry) => entry.verdict === verdict);
  return { changed: CHANGED_VERDICT_ORDER.flatMap(of), unclear: of('unclear'), stillHolds: of('still_holds') };
}

const PRIOR_VERDICT_PRESENTATION: Readonly<Record<SlackPriorVerdict, { tone: Tone; icon: IconName }>> = {
  resolved: { tone: 'success', icon: 'check' },
  overtaken: { tone: 'info', icon: 'clock' },
  no_longer_relevant: { tone: 'neutral', icon: 'minus' },
  unclear: { tone: 'warning', icon: 'help-circle' },
  still_holds: { tone: 'neutral', icon: 'dot' },
};

/** A verdict's pill: the same glyphs the meeting's Slack tab gives the same verdicts; never colour alone. */
export function briefPriorVerdictPresentation(verdict: SlackPriorVerdict): { tone: Tone; icon: IconName; label: string } {
  return { ...PRIOR_VERDICT_PRESENTATION[verdict], label: SLACK_PRIOR_VERDICT_LABELS[verdict] };
}

/** What kind of entry of the last brief was judged, named as the brief's sections name them. */
export function briefPriorSubjectKind(prior: AssistantBriefPriorSubject): string {
  switch (prior.type) {
    case 'thread':
      return 'Ongoing matter';
    case 'commitment':
      return prior.side === 'you_owe' ? 'You owed' : 'Owed to you';
    case 'inconsistency':
      return 'Did not agree';
    case 'watch_out':
      return 'Watch-out';
    case 'action_item':
    default:
      return 'Action item';
  }
}

export function briefPriorSubjectText(prior: AssistantBriefPriorSubject): string {
  return prior.type === 'thread' || prior.type === 'inconsistency' ? prior.title : prior.text;
}

/** "compared with your brief of 15 Sep 2026, 14:10 BST", for the provenance line; null when no earlier brief was judged. */
export function briefComparedWithText(comparedWith: Pick<AssistantBriefComparedWith, 'completedAt'> | null, timeZone?: string): string | null {
  return comparedWith ? `compared with your brief of ${formatDateTime(comparedWith.completedAt, timeZone ? { timeZone } : undefined)}` : null;
}

/**
 * The words on the disclosure's summary: a verb phrase, as the page's other disclosure triggers are ("Show the
 * message"). Why no count: the line above already gives the figures, and the disclosure exists only when something
 * was settled or could not be judged, so there is never a zero to say.
 */
export function briefReviewDisclosureText(groups: Pick<BriefPriorReviewGroups, 'changed' | 'unclear'>): string {
  const settled = groups.changed.length > 0;
  const unclear = groups.unclear.length > 0;
  if (settled && unclear) return 'Show what was settled, and what could not be judged';
  if (unclear) return 'Show what could not be judged';
  return 'Show what was settled';
}

// --- The head: Refresh recent meetings ---------------------------------------------------------------

/**
 * "Refreshing 23 meetings · 5 summarised · 3 of 5 checked against Slack · 2 summaries failed". Why "of N
 * … against Slack": "3 checked" alone did not say what a check is, nor how many there will be (the owner
 * asked, 2026-09-28). A check is counted once it has finished — also when Slack held nothing relevant and
 * no AI call was needed; N is the checks queued so far, since each is chained as its summary completes,
 * and the part is left out until the first one is. The failures are named by kind, so the person knows
 * whether a summary or a Slack check is the one to look at.
 */
export function refreshProgressText(progress: Pick<AssistantRefreshProgress, 'summaries' | 'checks'>): string {
  const parts = [
    `Refreshing ${pluralise(progress.summaries.total, { one: '# meeting', other: '# meetings' })}`,
    `${formatCount(progress.summaries.done)} summarised`,
  ];
  if (progress.checks.total > 0) parts.push(`${formatCount(progress.checks.done)} of ${formatCount(progress.checks.total)} checked against Slack`);
  if (progress.summaries.failed > 0) parts.push(pluralise(progress.summaries.failed, { one: '# summary failed', other: '# summaries failed' }));
  if (progress.checks.failed > 0) parts.push(pluralise(progress.checks.failed, { one: '# Slack check failed', other: '# Slack checks failed' }));
  return parts.join(' · ');
}

/**
 * The toast once "Refresh recent meetings" is accepted: how many meetings, and how many were already being summarised
 * where any were. Why whole templates (05-content §8; the record's "Assistant's refreshing line", Phase 1c D30): the
 * parenthesis was spliced in by condition, its verb a plural form of its own ("# was", "# were").
 */
export function refreshStartedText(meetings: number, joined: number): string {
  if (joined <= 0) {
    return pluralise(meetings, { one: 'Refreshing # meeting — the progress shows at the top of this page.', other: 'Refreshing # meetings — the progress shows at the top of this page.' });
  }
  const count = pluralise(meetings, { one: '# meeting', other: '# meetings' });
  const already = pluralise(joined, { one: '# was already being summarised', other: '# were already being summarised' });
  return `Refreshing ${count} (${already}) — the progress shows at the top of this page.`;
}

/**
 * What the confirmation says will happen (owner ruling A11a): the count, the two steps, what leaves, what
 * is kept — and that the refresh waits its turn. Why say so (production v1.71): a refresh's work yields to
 * live work (queue.ts, migration 0015), so the two steps alone would promise a pace it does not keep. Why
 * "one at a time" (ADR 0013, owner ruling 8): the worker runs three jobs at once, but a person's own
 * summaries and checks still run one at a time — the slots keep other people from waiting behind a refresh,
 * they do not speed the refresh itself, and the dialog must not read as that promise. Why it does not say
 * that several people's work runs at once: the client does not know the slot count, and at
 * `WORKER_CONCURRENCY=1`, the documented rollback, that would be false.
 *
 * Why the project sentence, and only where projects are on (`MeResponse.integrations.projects`; ADR 0017, design §3.5):
 * a refresh sends the person's project names with every summary — new egress the confirmation must name beside the
 * transcripts it already names — and sorts the meetings and their to-dos into projects again. With the floor off no
 * name leaves and nothing is sorted (owner ruling 9 of 2026-10-02), so saying so would be false. Why "and check" where
 * the checks run: the person reading this holds `ai_summaries` (the Assistant's own gate), so each Slack check of the
 * refresh carries the names too (slack/context-job.ts) — a sentence naming the summaries alone would understate it.
 */
export function refreshMeetingsText(input: { meetings: number; windowDays: number; slackIncluded: boolean; projectsOn: boolean }): { title: string; confirm: string; body: string[] } {
  const count = pluralise(input.meetings, { one: '# meeting', other: '# meetings' });
  const yields = 'New meetings, syncs and other people’s work go first, and your own meetings go one at a time, so it can take a while.';
  const leaves = input.slackIncluded
    ? 'Their transcripts, and the Slack messages Calis reads for each check, leave Calis for the AI provider — as they did when the meeting was first processed.'
    : 'Their transcripts leave Calis for the AI provider, as they did when the meeting was first processed.';
  return {
    title: `Refresh ${count}?`,
    confirm: `Refresh ${count}`,
    body: [
      input.slackIncluded
        ? `Every meeting from the last ${formatCount(input.windowDays)} days with a transcript is summarised again and then checked against Slack again. ${yields}`
        : `Every meeting from the last ${formatCount(input.windowDays)} days with a transcript is summarised again. ${yields} Slack checks are not available to you at the moment, so none run.`,
      input.projectsOn ? `${leaves} ${input.slackIncluded ? REFRESH_PROJECTS_WITH_SLACK_SENTENCE : REFRESH_PROJECTS_SENTENCE}` : leaves,
      'To-dos you have edited, ticked or dismissed are kept. Untouched to-dos Calis made are replaced by what the new summaries and checks find.',
    ],
  };
}

/** What a refresh adds to what leaves and what changes, under the projects floor (design §3.5; pinned by test). */
export const REFRESH_PROJECTS_SENTENCE = 'Your project names go with each summary, and the meetings and their to-dos are sorted into your projects again.';

/** The same, where the refresh checks each meeting against Slack as well: the checks carry the names too. */
export const REFRESH_PROJECTS_WITH_SLACK_SENTENCE = 'Your project names go with each summary and check, and the meetings and their to-dos are sorted into your projects again.';

// --- Action items ------------------------------------------------------------------------------------

export interface ActionItemGroup {
  kind: AssistantActionItemsResponse['groups'][number]['kind'];
  /**
   * The group's key on the page (its anchor `assistant-group-<key>`): the project's id or `no-project` under the
   * projects floor; else the meeting's id, `slack-day`, or `none` (a removed meeting's).
   */
  key: string;
  /** Its heading: the project's name or "No project yet"; else the meeting's title, "From your Slack day", or "A meeting that was removed". */
  title: string;
  meeting: AssistantActionItemsResponse['groups'][number]['meeting'];
  project: AssistantActionItemsResponse['groups'][number]['project'];
  /**
   * Whether the tab groups by project (ADR 0017, owner ruling 13 of 2026-10-02): then a group no longer names a meeting,
   * so each to-do names its own (`actionItemMeta`).
   */
  byProject: boolean;
  items: TodoDto[];
  notedTodos: TodoDto[];
}

/**
 * The heading of the trailing group of to-dos filed under no project (design §3.4, §11 item 7): "yet", since Calis
 * sorts them as it next meets them and the person may file them by hand.
 */
export const NO_PROJECT_GROUP_TITLE = 'No project yet';

/**
 * Where an action item came from, said beside it when the tab groups by project (design §3.4): its meeting's title, or
 * "Your Slack day" for the daily sweep's own — the owner's Q5 rule ("the to-do list shows them in their own group")
 * survives as this line, where the group no longer says it — or, tied to no meeting, what the To-dos list says there.
 */
export function actionItemMeta(todo: Pick<TodoDto, 'meeting' | 'fromSweep' | 'source'>): string {
  if (todo.meeting) return todo.meeting.title;
  if (todo.fromSweep) return SLACK_DAY_LABEL;
  return todoWithoutMeetingLabel(todo);
}

/** The words a group says of itself: when it has nothing left to decide on, and above and on its noted to-dos. */
export function actionGroupTexts(group: Pick<ActionItemGroup, 'kind' | 'title'>): { nothingLeft: string; notedLead: string; notedLabel: string } {
  switch (group.kind) {
    case 'slack_day':
      return { nothingLeft: 'Nothing left to decide on from your Slack day.', notedLead: 'What Slack shows about your to-dos that belong to no meeting', notedLabel: 'From Slack notes on your to-dos without a meeting' };
    case 'project':
      return { nothingLeft: 'Nothing left to decide on in this project.', notedLead: 'What Slack shows about this project’s other to-dos', notedLabel: `From Slack notes on the to-dos of ${group.title}` };
    case 'none':
      return { nothingLeft: 'Nothing left to decide on outside your projects.', notedLead: 'What Slack shows about your other to-dos in no project', notedLabel: 'From Slack notes on your to-dos in no project' };
    default:
      return { nothingLeft: 'Nothing left to decide on for this meeting.', notedLead: 'What Slack shows about this meeting’s other to-dos', notedLabel: `From Slack notes on the to-dos of ${group.title}` };
  }
}

/**
 * The heading of the group of the daily sweep's to-dos, tied to no meeting (owner ruling Q5). The heading, the
 * "From Slack" chip on every row and the absent "Meeting summary" aside say all there is: no lead beneath it.
 */
export const SLACK_DAY_GROUP_TITLE = 'From your Slack day';

/**
 * The Action items tab with nothing to decide on: where its to-dos come from. The daily look leads only where it writes
 * them (`SLACK_SWEEP_MODE=on`, `MeResponse.integrations.slackSweep`): there it is where most of the tab's items come
 * from, and a person with no recent meeting has no other source.
 */
export function actionItemsEmptyText(sweepOn: boolean): string {
  const sources = sweepOn ? 'When Calis looks at your Slack each day, checks a meeting against Slack, or writes your brief across meetings' : 'When Calis checks a meeting against Slack, or writes your brief across meetings';
  // Why no more: the chips, "Why?" and the tick are on the rows, which an empty list does not show (05-content §5).
  return `${sources}, what there is to do about it lands here as to-dos.`;
}

/**
 * Why `no-project` and not `none` for the trailing group: `none` already anchors a removed meeting's group with the
 * floor off, and an address kept from one reading must not open a different group in the other.
 */
function actionGroupKey(group: AssistantActionItemsResponse['groups'][number]): string {
  if (group.kind === 'project' && group.project) return group.project.id;
  if (group.kind === 'none') return 'no-project';
  if (group.kind === 'slack_day') return 'slack-day';
  return group.meeting?.id ?? 'none';
}

function actionGroupTitle(group: AssistantActionItemsResponse['groups'][number]): string {
  if (group.kind === 'project' && group.project) return group.project.name;
  if (group.kind === 'none') return NO_PROJECT_GROUP_TITLE;
  if (group.kind === 'slack_day') return SLACK_DAY_GROUP_TITLE;
  return group.meeting?.title ?? 'A meeting that was removed';
}

/**
 * The groups as the tab shows them, in the server's order — by project, by name, then the to-dos in no project, under
 * the projects floor; else the daily sweep's to-dos first, then newest meeting first, then the items whose meeting was
 * removed — each with its key and heading.
 */
export function actionItemGroups(response: Pick<AssistantActionItemsResponse, 'groups'>): ActionItemGroup[] {
  return response.groups.map((group) => ({
    kind: group.kind,
    key: actionGroupKey(group),
    title: actionGroupTitle(group),
    meeting: group.meeting,
    project: group.project,
    byProject: group.kind === 'project' || group.kind === 'none',
    items: group.items,
    notedTodos: group.notedTodos,
  }));
}

/**
 * A group's count: its items and the meeting's other to-dos Slack noted. Why both (04-data §1.6): a group kept only for
 * its noted to-dos would read "0" and look empty once folded.
 */
export function actionGroupCount(group: Pick<ActionItemGroup, 'items' | 'notedTodos'>): number {
  return group.items.length + group.notedTodos.length;
}

/** What the count counts, for a screen reader: "2 items to decide on, 1 more to-do with notes from Slack". */
export function actionGroupCountLabel(group: Pick<ActionItemGroup, 'items' | 'notedTodos'>): string {
  const decide = pluralise(group.items.length, { one: '# item to decide on', other: '# items to decide on' });
  if (group.notedTodos.length === 0) return decide;
  return `${decide}, ${pluralise(group.notedTodos.length, { one: '# more to-do with notes from Slack', other: '# more to-dos with notes from Slack' })}`;
}

// --- Analysis ----------------------------------------------------------------------------------------

export interface SlackCheckPresentation {
  word: string;
  tone: Tone;
  icon: IconName;
  /** For sorting: never checked, then by how far it got. */
  rank: number;
  detail: string;
}

/** A meeting's Slack check at a glance, from its digest (05-content §6 words). */
export function slackCheckPresentation(digest: MeetingSlackContextDigest | null): SlackCheckPresentation {
  if (digest === null) return { word: 'Not checked', tone: 'neutral', icon: 'minus', rank: 0, detail: 'Calis has not looked in Slack for this meeting.' };
  switch (digest.status) {
    case 'queued':
      return { word: 'Pending', tone: 'info', icon: 'clock', rank: 1, detail: 'A Slack check is waiting to run.' };
    case 'running':
      return { word: 'Running', tone: 'info', icon: 'clock', rank: 2, detail: 'Calis is looking in Slack now.' };
    case 'failed':
      return { word: 'Failed', tone: 'danger', icon: 'alert-circle', rank: 3, detail: 'The last Slack check did not complete.' };
    case 'ready':
    default:
      // Why "Completed", as the Summary column beside it: one word per finished run (05-content §6); the detail keeps what it found.
      return { word: 'Completed', tone: 'success', icon: 'check', rank: 4, detail: digest.visibleCount === null ? 'Checked against Slack.' : `Checked against Slack: ${pluralise(digest.visibleCount, { one: '# finding', other: '# findings' })}.` };
  }
}

/** A finding count for the table: null until a check has completed, so "no check" never reads as 0. */
export function analysisFindingCount(row: Pick<AssistantAnalysisRow, 'findings'>, kind: SlackFindingKind): number | null {
  return row.findings === null ? null : row.findings[kind];
}

export function analysisActionItemCount(row: Pick<AssistantAnalysisRow, 'actionItems'>): number {
  return row.actionItems.fromSlack + row.actionItems.acrossMeetings;
}

/** Meeting titles by id, for naming a brief's references. */
export function meetingTitles(rows: readonly Pick<AssistantAnalysisRow, 'id' | 'title'>[]): Map<string, string> {
  return new Map(rows.map((row) => [row.id, row.title]));
}

// --- Slack today (owner request of 2026-09-30: the daily Slack sweep) -----------------------------------

export interface SlackTodayStateText {
  title: string;
  description: string;
  /** Where the person can change what stands in the way, when they can: their Slack connection. */
  connections: boolean;
}

/** Why the tab has nothing to show while the person may see Slack findings, and what would change it (03-components §1). */
export function slackTodayOffText(reason: SlackTodayOffReason | null): SlackTodayStateText {
  switch (reason) {
    case 'not_connected':
      return { title: 'Slack is not connected', description: 'Once you connect Slack, Calis looks through your day in Slack once a day and shows here what concerns you and your open matters.', connections: true };
    case 'needs_attention':
      return { title: 'Your Slack connection needs attention', description: 'Calis cannot read your Slack until you connect it again. Its daily look resumes once you do.', connections: true };
    case 'auto_check_off':
      return { title: 'Calis does not look at your Slack each day', description: 'You switched off “Check Slack automatically”. Switch it on in Connections and Calis looks through your day in Slack once a day.', connections: true };
    case 'no_sources':
      return { title: 'Calis has nothing it may read in Slack', description: 'Every channel you could let Calis read is left out, and direct messages and group chats are switched off. Choose what it may read in Connections.', connections: true };
    // Why not "off" (SLACK_SWEEP_MODE=measure): the person's day is read, and Connections says so; this tab must not
    // say otherwise. It says what is read, that only counts are kept, and where the switch is.
    case 'measuring':
      return {
        title: 'Calis only counts your day in Slack for now',
        description: `${SLACK_TODAY_READS} — but for now it only counts what it reads: it sends nothing anywhere and shows nothing here yet. Once it looks for what concerns you and your open matters, that appears here. “Check Slack automatically” in Connections turns the daily look off.`,
        connections: true,
      };
    case 'installation':
    default:
      return { title: 'The daily look at Slack is not switched on', description: 'Calis does not yet look through your day in Slack on this installation; when it does, what concerns you and your open matters appears here.', connections: false };
  }
}

/**
 * What the daily look reads (owner rulings Q6 and N1), as Connections says it: the conversations the person lets
 * Calis read and, through Slack's search, every public channel — never less than is read.
 */
const SLACK_TODAY_READS = 'Once a day, Calis reads what was said in the conversations you let it read, and in every public channel through Slack’s search';

/**
 * The latest daily look the person's own "Check Slack automatically" switch stopped (the owner's ruling Q6(a) of
 * escalation 20261005-200526): 05-content §6's Skipped with its reason beside the word; the instant follows it.
 */
export const SLACK_TODAY_SKIPPED_TEXT = 'Latest daily look: Skipped · automatic checks are off';

/** The running state: a look under way, and what it reads. */
export const SLACK_TODAY_RUNNING_TEXT = 'It is reading the conversations you let it read, and every public channel through Slack’s search. What concerns you and your open matters appears here when it is done.';

/**
 * No look to show yet: what would appear, and when the next look comes — after the channel list when the first one
 * waits for it; at the time it falls due when that is known (after looks that only counted, the next is a day later,
 * not "shortly"); otherwise at the next turn of the schedule.
 */
export function slackTodayNoneText(next: SlackTodayResponse['next'], now: Date, timeZone?: string): string {
  const shown = `${SLACK_TODAY_READS}, and shows here what concerns you and your open matters.`;
  if (next?.afterChannelList) return `${shown} The first look comes shortly after your Slack channel list is ready.`;
  if (next?.at) return `${shown} ${slackTodayNextText(next.at, now, timeZone)}.`;
  return `${shown} The first look comes shortly.`;
}

/** "Since 29 Sep 2026, 14:10 BST · 340 messages in 25 conversations" — what the latest look read. */
export function slackTodayReadText(sweep: Pick<SlackTodaySweepDto, 'first' | 'window' | 'read'>, timeZone?: string): string {
  const counts = `${pluralise(sweep.read.messages, { one: '# message', other: '# messages' })} in ${pluralise(sweep.read.conversations, { one: '# conversation', other: '# conversations' })}`;
  if (sweep.first) return `Your first look: the last 24 hours · ${counts}`;
  return `Since ${formatDateTime(sweep.window.start, timeZone ? { timeZone } : undefined)} · ${counts}`;
}

/**
 * "Next look after 1 Oct 2026, 14:10 BST" — the scheduler queues it at its first tick after that instant, so the
 * page never promises the minute; while the next look is under way it says so.
 */
export function slackTodayNextText(nextDueAt: string | null, now: Date, timeZone?: string): string {
  if (nextDueAt === null) return 'Calis is looking at your Slack again now';
  if (Date.parse(nextDueAt) <= now.getTime()) return 'Calis looks again shortly';
  return `Next look after ${formatDateTime(nextDueAt, timeZone ? { timeZone } : undefined)}`;
}

const SWEEP_PARTIAL_TEXT: Readonly<Record<SlackPartialReason, string>> = {
  call_budget: 'Calis stopped reading early to stay within the number of Slack requests one look may make.',
  time_budget: 'Calis stopped reading early because the look ran out of time.',
  rate_limited: 'Slack asked Calis to slow down, so it stopped reading early.',
  source_unreadable: 'Some conversations could not be read — you may have left a channel, or it was archived.',
  message_cap: 'There were more messages than one look reads, so Calis read as many as it may and stopped there.',
};

/**
 * What the latest look did not read, one sentence each, for the banner above its findings; empty when it read
 * everything. Why the public search is said whenever it is not `read` (owner ruling N1): the sweep reads every
 * public channel of the company through a search Slack does not document, and must never pass the person's own
 * channels off as the whole company. Why the gap is said (N2): after a long gap only the last three days are read.
 */
export function slackTodayGaps(sweep: Pick<SlackTodaySweepDto, 'partialReason' | 'sourcesUnreadable' | 'publicSearch' | 'skippedFrom' | 'window'>, timeZone?: string): string[] {
  const zone = timeZone ? { timeZone } : undefined;
  const gaps: string[] = [];
  if (sweep.skippedFrom !== null) {
    gaps.push(`Calis could not look at your Slack for a while, so it read only the last three days: messages from ${formatDateTime(sweep.skippedFrom, zone)} to ${formatDateTime(sweep.window.start, zone)} were not read.`);
  }
  if (sweep.partialReason === 'source_unreadable' && sweep.sourcesUnreadable > 0) {
    gaps.push(`${pluralise(sweep.sourcesUnreadable, { one: '# conversation', other: '# conversations' })} could not be read — you may have left a channel, or it was archived.`);
  } else if (sweep.partialReason !== null) {
    gaps.push(SWEEP_PARTIAL_TEXT[sweep.partialReason]);
  }
  if (sweep.publicSearch !== 'read') gaps.push(SLACK_SWEEP_PUBLIC_SEARCH_LABELS[sweep.publicSearch]);
  return gaps;
}

/** "A quiet day" — said only when the look found nothing open, so an empty tab is never mistaken for a broken one. */
export function slackTodayIsQuiet(sweep: Pick<SlackTodaySweepDto, 'matters' | 'updates' | 'todoNotes' | 'doneSignals'>): boolean {
  return sweep.matters.length === 0 && sweep.updates.length === 0 && sweep.todoNotes.length === 0 && sweep.doneSignals.length === 0;
}

/**
 * What became of a "Mark done" suggestion (owner ruling Q8(b)), as a pill on the tab's entry: never colour alone
 * (01-foundations §7.3). An offer standing waits on the person — the to-do's own row carries Mark done; the rest say
 * how it ended; with none made, that it was not offered.
 */
export function slackTodayOfferPresentation(offer: SlackTodayDoneSignalDto['offer']): { tone: Tone; icon: IconName; label: string } {
  switch (offer) {
    // Why "Open" and a qualifier: an offer standing awaits its owner's action, 05-content §6's Open; "Waiting" sits on
    // Pending's Not-list (conformity 2026-10-05, Phase 2's leftovers). The qualifier says whose the next act is.
    case 'open':
      return { tone: 'info', icon: 'clock', label: 'Open — yours to decide' };
    case 'accepted':
      return { tone: 'success', icon: 'check', label: 'You marked it done' };
    case 'dismissed':
      return { tone: 'neutral', icon: 'x', label: 'You said not done' };
    case 'closed':
      return { tone: 'neutral', icon: 'minus', label: 'No longer suggested' };
    // Why not "Not suggested": the section is "Suggested as done" — Slack did suggest it; Calis did not offer it (offered
    // or turned down on the same evidence before, or the to-do changed while the AI step answered).
    case null:
    default:
      return { tone: 'neutral', icon: 'minus', label: 'Not offered' };
  }
}

/**
 * The lead of "Slack today"'s "Suggested as done": Calis never ticks, and where the one control is — the Mark done
 * sits on the to-do's own row, not on this card, so an Open one needs the pointer (05-content §6). The rest of the
 * pills (`slackTodayOfferPresentation`) say for themselves how each offer ended, so the line says nothing of them.
 */
export const SLACK_TODAY_DONE_LEAD = 'Calis never ticks these off itself; one marked “Open” has Mark done on its to-do.';

/**
 * Where a note of the sweep's leads: its to-do on the To-dos page, highlighted, whatever its state — a verdict's to-do
 * is on the dismissed list (ruling Q5; ADR 0007 A13 widened).
 */
export function slackTodayTodoHref(todoId: string): string {
  return `${PATHS.todos}?status=all&highlight=${encodeURIComponent(todoId)}`;
}

const UPDATE_PRESENTATION: Readonly<Record<SlackSweepUpdateStatus, { tone: Tone; icon: IconName }>> = {
  progressed: { tone: 'info', icon: 'chevron-right' },
  resolved: { tone: 'success', icon: 'check' },
  blocked: { tone: 'danger', icon: 'alert-triangle' },
  overtaken: { tone: 'info', icon: 'clock' },
  unchanged_but_active: { tone: 'neutral', icon: 'dot' },
};

/**
 * An update's pill: never colour alone (01-foundations §7.3). Its words are readings of what Slack shows, never
 * 05-content §6's state words (§6.1, ruled A25 in design system 8.5.0; `SLACK_SWEEP_UPDATE_STATUS_LABELS`).
 */
export function slackTodayUpdatePresentation(status: SlackSweepUpdateStatus): { tone: Tone; icon: IconName; label: string } {
  return { ...UPDATE_PRESENTATION[status], label: SLACK_SWEEP_UPDATE_STATUS_LABELS[status] };
}

/** What kind of entry of the brief an update is about, named as the brief's sections name them. */
export function slackTodayUpdateKind(type: SlackTodayUpdateDto['subjectType']): string {
  switch (type) {
    case 'thread':
      return 'Ongoing matter';
    case 'commitment':
      return 'Commitment';
    case 'inconsistency':
      return 'Did not agree';
    case 'watch_out':
    default:
      return 'Watch-out';
  }
}

/** The messages an entry cites, in the order it cites them; an id the payload does not hold is passed over. */
export function slackTodayCitationsOf(citationIds: readonly string[], citations: readonly SlackTodayCitationDto[]): SlackTodayCitationDto[] {
  const byId = new Map(citations.map((citation) => [citation.id, citation]));
  return citationIds.flatMap((id) => {
    const citation = byId.get(id);
    return citation ? [citation] : [];
  });
}

/**
 * A matter's (`m…`, a suffix when two share their words), an update's (`u…`), a note's (`n…`) or a done signal's (`d…`)
 * id: the sweep's own (ai/slack-sweep/validate.ts; shared/slack.ts SLACK_SWEEP_ITEM_ID_PATTERN).
 */
const SLACK_TODAY_HASH = /^#st-([mund][0-9a-f]{16}(?:-\d{1,3})?)$/u;

/** The finding a link to the Slack today tab points at (`#st-<id>`, from a reference in the brief), or null. */
export function slackTodayTarget(hash: string): string | null {
  return SLACK_TODAY_HASH.exec(hash)?.[1] ?? null;
}

/** The tab's count: what is open in the latest look, null until there is one to count. */
export function slackTodayCount(response: Pick<SlackTodayResponse, 'state' | 'sweep'> | null): number | undefined {
  return response?.state === 'ready' && response.sweep
    ? response.sweep.matters.length + response.sweep.updates.length + response.sweep.todoNotes.length + response.sweep.doneSignals.length
    : undefined;
}
